/**
 * @fileoverview Shared callable API contract for
 * `recordSuperSectionAttendance`, which records a Super Section student's
 * join and the section recordings a student marks as watched. Imported by
 * both the functions handler and the webapp client so the two sides cannot
 * drift in name, request shape, or response shape.
 *
 * A Super Section is a webinar with no section leader to mark attendance, and
 * its students reach it only through the Section page's join button, so the
 * click is the attendance signal. A recording marked watched counts only with
 * that week's teach-back (see `sectionWeekCredit.ts`), and small-section
 * students mark one for a week they missed through the same callable. The
 * callable keeps its Super Section name because renaming a deployed function
 * means a new function. Firestore rules let only staff write a student's
 * attendance documents, which is why every mark goes through a callable. See
 * [#5174](https://github.com/CodeinPlace/cip/issues/5174) and
 * [#5307](https://github.com/CodeinPlace/cip/issues/5307).
 */
import {z} from 'zod';

/**
 * Deployed Cloud Function name. Must match the exported function name in the
 * `sections` functions codebase so deployment and invocation resolve to the
 * same function.
 */
export const RECORD_SUPER_SECTION_ATTENDANCE_FUNCTION_NAME =
  'recordSuperSectionAttendance';

/** A single Firestore document id: non-empty and free of `/`. */
const DocumentIdSchema = z
  .string()
  .min(1)
  .refine(value => !value.includes('/'), {
    message: 'must not contain "/"',
  });

/**
 * What the client sends. A `join` click records the meeting whose window is
 * open now, so it names no week. A `recording` marks a Super Section week's
 * recording watched. A `catchUp` marks a recording watched for a week the
 * caller missed in their own small section, which the server looks up.
 */
export const RecordSuperSectionAttendanceRequestSchema = z.discriminatedUnion(
  'kind',
  [
    z.object({
      kind: z.literal('join'),
      /** The course the Super Section belongs to. */
      courseId: DocumentIdSchema,
      /** The Super Section time slot whose join button was clicked. */
      superSectionId: DocumentIdSchema,
    }),
    z.object({
      kind: z.literal('recording'),
      /** The course the Super Section belongs to. */
      courseId: DocumentIdSchema,
      /** The Super Section time slot whose recording was watched. */
      superSectionId: DocumentIdSchema,
      /** The 0-based course week the recording is of. */
      weekIndex: z.number().int().nonnegative(),
    }),
    z.object({
      kind: z.literal('catchUp'),
      /** The course of the caller's small section. */
      courseId: DocumentIdSchema,
      /** The 0-based course week the caller missed. */
      weekIndex: z.number().int().nonnegative(),
    }),
    z.object({
      kind: z.literal('check'),
      /** The course. */
      courseId: DocumentIdSchema,
      /** The 0-based course week whose check the caller answered. */
      weekIndex: z.number().int().nonnegative(),
      /**
       * One answer per question, in order: the chosen option's index for a
       * choice question, the number for a number question, `null` for one
       * left blank.
       */
      answers: z.array(z.number().nullable()),
    }),
  ],
);

/** Parsed request. */
export type RecordSuperSectionAttendanceRequest = z.infer<
  typeof RecordSuperSectionAttendanceRequestSchema
>;

/**
 * Why a call recorded nothing. The join button ignores these, since opening
 * the meeting must never depend on attendance succeeding; the recording
 * buttons show them.
 */
export type RecordSuperSectionAttendanceRefusal =
  /** The week was already recorded for this student; nothing changed. */
  | 'already-recorded'
  /**
   * `superSectionId` is not one of the course's `superSectionIds`, is not a
   * Super Section of that course, or has no first meeting configured.
   */
  | 'unknown-super-section'
  /**
   * The caller is not a student of the course, or is a student placed in a
   * small section rather than the Super Section.
   */
  | 'not-super-section-student'
  /**
   * A `catchUp` from a caller who is not a student of the course, or who has
   * no small section in it.
   */
  | 'not-section-student'
  /** A `join` outside the join window of the nearest meeting. */
  | 'outside-window'
  /** A `recording` for a week before the Super Section's first meeting. */
  | 'unknown-week'
  /** A `recording` for a week whose meeting has not started yet. */
  | 'week-not-started'
  /** A `recording` or `catchUp` for a meeting that has not ended yet. */
  | 'week-not-ended'
  /** A `check` for a week whose schedule sets no check. */
  | 'no-check'
  /** A `check` with a wrong or missing answer; `results` says which. */
  | 'wrong-answers';

/** What the callable returns. */
export type RecordSuperSectionAttendanceResponse =
  | {
      /** The week was newly recorded. */
      recorded: true;
      /** The 0-based course week it was recorded against. */
      weekIndex: number;
      /** For a `check`: whether each answer was right, in question order. */
      results?: boolean[];
    }
  | {
      /** Nothing was written. */
      recorded: false;
      /**
       * The 0-based course week the call resolved to, or `null` when the
       * refusal came before a week could be determined.
       */
      weekIndex: number | null;
      /** Why. */
      reason: RecordSuperSectionAttendanceRefusal;
      /** For `wrong-answers`: whether each answer was right, in question order. */
      results?: boolean[];
    };

/**
 * Path of a student's per-course attendance document: a map from 0-based
 * course week to whether they attended. Small-section joins write the same
 * document, so staff views read Super Section students like any other.
 *
 * @param params.userId - The student.
 * @param params.courseId - The course.
 * @return The document path.
 * @throws Never.
 */
export function getUserSectionAttendanceDocumentPath({
  userId,
  courseId,
}: {
  userId: string;
  courseId: string;
}): string {
  return `users/${userId}/${courseId}/sectionAttendance`;
}

/**
 * Path of a student's per-course document mapping each attended 0-based
 * course week to the ids of the sections they attended it in.
 *
 * @param params.userId - The student.
 * @param params.courseId - The course.
 * @return The document path.
 * @throws Never.
 */
export function getUserSectionAttendanceIdsDocumentPath({
  userId,
  courseId,
}: {
  userId: string;
  courseId: string;
}): string {
  return `users/${userId}/${courseId}/sectionAttendanceIds`;
}

/**
 * Path of a student's per-course document marking the 0-based course weeks
 * whose section recording they marked as watched. A sibling of the attendance
 * document with the same week-to-boolean shape, kept apart so that watching a
 * recording is never mistaken for attending live.
 *
 * @param params.userId - The student.
 * @param params.courseId - The course.
 * @return The document path.
 * @throws Never.
 */
export function getUserSectionRecordingAttendanceDocumentPath({
  userId,
  courseId,
}: {
  userId: string;
  courseId: string;
}): string {
  return `users/${userId}/${courseId}/sectionRecordingAttendance`;
}

/**
 * Path of a student's per-course document mapping each 0-based course week in
 * `sectionRecordingAttendance` to the ids of the sections whose recording they
 * marked: a Super Section, or their own small section for a catch-up. The
 * credit rule caps only the small-section weeks.
 *
 * @param params.userId - The student.
 * @param params.courseId - The course.
 * @return The document path.
 * @throws Never.
 */
export function getUserSectionRecordingAttendanceIdsDocumentPath({
  userId,
  courseId,
}: {
  userId: string;
  courseId: string;
}): string {
  return `users/${userId}/${courseId}/sectionRecordingAttendanceIds`;
}

/**
 * Path of a student's per-course document recording the catch-up checks they
 * passed: a map from 0-based course week to `{completedAt, kind}`, written
 * only by this callable once every answer is right. The credit rule reads it
 * next to the SimTeach puzzle summary, so either kind of exercise counts.
 *
 * @param params.userId - The student.
 * @param params.courseId - The course.
 * @return The document path.
 * @throws Never.
 */
export function getUserSectionExercisesDocumentPath({
  userId,
  courseId,
}: {
  userId: string;
  courseId: string;
}): string {
  return `users/${userId}/${courseId}/sectionExercises`;
}
